import { Observable } from 'rxjs';
import { GroupCheckbox } from '../../../modules/settings/notification-settings/notification-settings.interface';
import { ITableView } from '../../components/facade/service-facade/data.response';
import { TermModel } from '../../modals/settings-statistic/settings-statistic.interface';
import { PaymentType } from '../../components/form-fields/payment-account/enums/payment-types.enum';
import { IValue } from '@platform500services/p500-ui-kit';
import { InjectionToken } from '@angular/core';

export interface IDataService {
  setData(data: any): void;

  getDataFromServer(): Observable<any>;

  getData(): IParsedDataResponse;
}

interface IPermission {
  name: string;
  items: {
    key: string;
    name: string;
  };
}

export interface ICountries {
  [country: string]: { code: string; name: string; img: string; prefix: string; currency_code: string; regions?: IKeyValueObject };
}

export interface ILanguages {
  [language: string]: {
    code: string;
    name: string;
    img: string;
  };
}

export interface ITimezones {
  [timezone: string]: { value: number | string; name: string };
}

export type ConversionProfileFieldsFormats = 'text' | 'number' | 'money' | 'email' | 'date' | 'boolean';

interface IKeyValueObject {
  [key: string]: any;
}

export interface IDataResponse {
  data: {
    version: string;
    advertisers: object;
    advertisers_active: object;
    affiliates: object;
    goals: object;
    offers: object;
    offer_goals: IKeyValueObject;
    permissions: IPermission[];
    roles: object;
    tracking_domains: object;
    users: object;
    invoice_currency_codes: string[];
    invoice_payment_systems: string[];
    invoice_statuses: string[];
    terms: IDataTerms;
    goal_types: { id: number; name: string }[];
    payout_models: PayoutModels;
    revenue_models: PayoutModels;
    business_model_formats: object;
    terms_format: IKeyValueObject;
    crm_document_types: IKeyValueObject;
    crm_question_types: IKeyValueObject;
    crm_partner_types: IKeyValueObject;
    affiliates_active: IKeyValueObject;
    advertiser_providers: IKeyValueObject;
    affiliate_groups: IKeyValueObject;
    oauth: {
      slack: {
        client_id: string;
        redirect_uri: string;
      };
    };
    affiliate_managers: IKeyValueObject;
    affiliate_trackers_placeholder: TermsPlaceholders;
    offer_url_placeholders: GroupedTermsPlaceholders;
    advertiser_system_roles: IKeyValueObject;
    advertiser_brands: IKeyValueObject;
    advertiser_brands_map: IKeyValueObject;
    available_affiliates: IKeyValueObject;
    agreement_status: IKeyValueObject;
    available_offer_source: IKeyValueObject;
    available_offer_category: IKeyValueObject;
    offer_redirect_types: IKeyValueObject;
    postback_status: IKeyValueObject;
    offer_privacy: IKeyValueObject;
    offer_status: IKeyValueObject;
    offer_urls: IKeyValueObject;
    notificationChannelMapper: GroupCheckbox[];
    drill_down_menu: IDrillDownMenu[];
    baseline_fields: IKeyValueObject;
    offer_urls_free_parameters: IKeyValueObject;
    default_currency: IDefaultCurrency;
    baseline_types: IKeyValueObject;
    table_views: ITableView[];
    kyc_states: IKeyValueObject;
    kyc_types: IKeyValueObject;
    kyc_approve_modes: IKeyValueObject;
    schemas: IKeyValueObject;
    integrations: IIntegration[];
    public_ip: string;
    transaction_types: IKeyValueObject;
    no_negative_periods: IKeyValueObject;
    exclude_clicks_fields: IKeyValueObject;
    exclude_conversions_fields: IKeyValueObject;
    aff_crm_exclude_clicks_fields: IKeyValueObject;
    aff_crm_exclude_offers_fields: IKeyValueObject;
    aff_crm_exclude_conversions_fields: IKeyValueObject;
    aff_crm_include_conversions_fields: IKeyValueObject;
    conversion_profile_fields: ConversionProfileFields;
    payment_systems: { name: keyof typeof PaymentType; currencies: string[] }[];
    currency_list: ICurrency;
    payment_systems_list: string[];
    default_payment_systems_list: IDefaultPaymentSystems;
    postback_context: IKeyValueObject;
    description_presets: IOfferDescriptionPreset[];
    offer_requests_statuses: IKeyValueObject;
    referral_payout_models: IReferralPayoutModel;
    referral_programs: IReferralProgramModel[];
    currency_provider: IKeyValueObject;
    default_currencies: IKeyValueObject;
    currency_integrations: ICurrencyIntegration[];
    conversion_profile_fields_placeholders: string[];
    earning_total_formula_placeholders: string[];
    affiliate_clusters: { [key: number]: string };
    pending_unassign_affiliates: Record<number, string>;
    finance_earning_statuses: Record<EARNING_STATUSES, string>;
    finance_invoice_statuses: Record<INVOICE_STATUSES, string>;
    isUnpaid: boolean;
    available_offer_currencies: string[];
    verticals: IKeyValueObject;
    tokens_type: IKeyValueObject;
    activity_status_boolean: IKeyValueObject;
    conversion_profile_fields_for_formula_placeholders: string[];
    user_activity_log_targets: IKeyValueObject;
    user_activity_log_actions: IKeyValueObject;
    user_activity_log_methods: IKeyValueObject;
  };
}

export interface IOfferDescriptionPreset {
  id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface PayoutModels {
  [key: number]: {
    short_name: string;
    name: string;
    hint: string;
  };
}

export interface IDefaultPaymentSystems {
  [key: number]: {
    name: string;
    currencies: string[];
  };
}

export interface PaymentSystem {
  name: keyof typeof PaymentType;
  codes: Record<string, any>;
}

export interface IDataTerms {
  [key: number]: TermModel;
}

export interface IParsedDataResponse {
  version: string;
  countries: ICountries;
  timezones: ITimezones;
  advertisers: IKeyValueObject;
  advertisers_active: IKeyValueObject;
  affiliates: IKeyValueObject;
  goals: object;
  offers: IKeyValueObject;
  offer_goals: IKeyValueObject;
  permissions: IPermission[];
  roles: object;
  tracking_domains: object;
  users: object;
  users_without_tech: object;
  months: object;
  invoice_currency_codes: string[];
  invoice_payment_systems: string[];
  countriesWithoutAll: Countries;
  payout_type: object;
  revenue_type: object;
  referral_payout_type: object;
  terms: IDataTerms;
  user_status: object;
  advertiser_status: object;
  conversion_tracking_type: object;
  affiliate_tracker_type: object;
  goal_types: object;
  payout_labels: object;
  revenue_labels: object;
  referral_payout_labels: object;
  business_model_formats: object;
  languages: ILanguages;
  terms_format: IKeyValueObject;
  crm_document_types: IKeyValueObject;
  crm_question_types: IKeyValueObject;
  crm_partner_types: IKeyValueObject;
  affiliates_active: IKeyValueObject;
  advertiser_providers: IKeyValueObject;
  affiliate_groups: IKeyValueObject;
  oauth: {
    slack: {
      client_id: string;
      redirect_uri: string;
    };
  };
  affiliate_managers: IKeyValueObject;
  affiliate_trackers_placeholder: TermsPlaceholders;
  offer_url_placeholders: GroupedTermsPlaceholders;
  advertiser_system_roles: IKeyValueObject;
  advertiser_brands: IKeyValueObject;
  advertiser_brands_map: IKeyValueObject;
  affiliate_manager_affiliates: IKeyValueObject;
  agreement_status: IKeyValueObject;
  available_offer_source: IKeyValueObject;
  available_offer_category: IKeyValueObject;
  offer_redirect_types: IKeyValueObject;
  postback_status: IKeyValueObject;
  offer_privacy: IKeyValueObject;
  offer_status: IKeyValueObject;
  offer_urls_by_offer: IKeyValueObject;
  offer_urls: IKeyValueObject;
  drill_down_menu: IDrillDownMenu[];
  baseline_fields: IKeyValueObject;
  offer_urls_free_parameters: IKeyValueObject;
  default_currency: IDefaultCurrency;
  treeMoneyFormat: IKeyValueObject;
  baseline_types: IKeyValueObject;
  table_views: ITableView[];
  kyc_states: IKeyValueObject;
  kyc_types: IKeyValueObject;
  kyc_approve_modes: IKeyValueObject;
  schemas: IKeyValueObject;
  advertiser_integrations: IIntegration[];
  public_ip: string;
  transaction_types: IKeyValueObject;
  no_negative_periods: IKeyValueObject;
  hoursFromData: IKeyValueObject;
  hoursToData: IKeyValueObject;
  exclude_clicks_fields: IKeyValueObject;
  exclude_conversions_fields: IKeyValueObject;
  aff_crm_exclude_clicks_fields: IKeyValueObject;
  aff_crm_exclude_offers_fields: IKeyValueObject;
  aff_crm_exclude_conversions_fields: IKeyValueObject;
  aff_crm_include_conversions_fields: IKeyValueObject;
  dayOfWeek: string[];
  payment_statuses: IKeyValueObject;
  earning_statuses: IKeyValueObject;
  invoice_statuses: IKeyValueObject;
  payment_systems: PaymentSystem[];
  currency_list: ICurrency;
  payment_systems_list: string[];
  default_payment_systems_list: IDefaultPaymentSystems;
  postback_context: IKeyValueObject;
  conversion_profile_fields: ConversionProfileFields;
  conversionProfileFormats: IKeyValueObject;
  description_presets: IOfferDescriptionPreset[];
  offer_requests_statuses: IKeyValueObject;
  referral_payout_models: IReferralPayoutModel;
  referral_programs: IReferralProgramModel[];
  referral_payout_models_labels: Record<string, string>;
  payout_models: PayoutModels;
  revenue_models: PayoutModels;
  currency_provider: IKeyValueObject;
  currency_integrations: ICurrencyIntegration[];
  default_currencies: IKeyValueObject;
  default_privacy_options: IKeyValueObject;
  conversion_profile_fields_placeholders: string[];
  earning_total_formula_placeholders: string[];
  affiliate_clusters: { [key: number]: string };
  available_languages: Record<string, IAvailableLanguage>;
  current_language: string;
  pending_unassign_affiliates: Record<number, string>;
  days: Record<string, any>;
  finance_earning_statuses: Record<EARNING_STATUSES, string>;
  finance_invoice_statuses: Record<INVOICE_STATUSES, string>;
  isUnpaid: boolean;
  verticals: IKeyValueObject;
  default_currencies_options: ICurrency;
  available_offer_currencies: string[];
  offer_currencies_options: ICurrency;
  tokens_type: IKeyValueObject;
  activity_status_boolean: IKeyValueObject;
  conversion_profile_fields_for_formula_placeholders: string[];
  offer_advertisers: Record<number, number>;
  languages_with_all: ILanguages;
  user_activity_log_targets: Record<string, string>;
  user_activity_log_actions: Record<string, string>;
  user_activity_log_methods: Record<number, LOG_METHODS>;
}

export interface IAvailableLanguage {
  code: string;
  name: string;
}

export interface Countries {
  [key: string]: {
    code: string;
    currency_code: string;
    img: string;
    name: string;
    prefix: string;
  };
}

export interface ICurrency {
  [key: string]: { code: string; name: string; img: string };
}

interface ConversionProfileFields {
  [key: string]: {
    id: number;
    key: string;
    label: string;
    format: ConversionProfileFieldsFormats;
    groups: any;
    active: boolean;
    protected: boolean;
    created_at: string;
    updated_at: string;
    type: string;
  };
}

interface IDrillDownMenu {
  hash: string;
  name: string;
}

type TermsPlaceholders = Array<string> | Array<{ [key: string]: string[] }>;

export type GroupedTermsPlaceholders = {
  [key: string]: string[];
};

interface IDefaultCurrency {
  currency?: string;
  position?: 'left' | 'right';
}

export interface IIntegration {
  id: number;
  name: string;
  type: string;
  placeholders: any[];
}

export type ISortedColumns = string[];

export const IDATA_SERVICE = new InjectionToken<IDataService>('IDataService');

export interface IConstants {
  timezones: ITimezones;
  languages: ILanguages;
  countries: ICountries;
  months: IKeyValueObject;
  hoursFromData: IKeyValueObject;
  hoursToData: IKeyValueObject;
  dayOfWeek: string[];
  availableLanguages: Record<string, IAvailableLanguage>;
}

export interface IReferralPayoutModel {
  [id: string]: {
    hint: string;
    name: string;
    short_name: string;
    placeholder: string;
  };
}

export interface IReferralProgramModel {
  name: string;
  id: number;
}

export enum EReferralProgramType {
  REFERRAL_BONUS = 'referral_bonus',
  REFERRAL_CORRECTION = 'referral_correction',
}

export const REFERRAL_PROGRAM_TYPE = {
  [EReferralProgramType.REFERRAL_BONUS]: $localize`:@@common.referral_bonus:Referral Bonus`,
  [EReferralProgramType.REFERRAL_CORRECTION]: $localize`:@@common.referral_correction:Referral Correction`,
};

export enum EOfferUrlSourceType {
  OTHER = 'Other',
  LD = 'LD',
}

export const OFFER_URL_SOURCE_TYPES = {
  [EOfferUrlSourceType.OTHER]: $localize`:@@common.other:Other`,
  [EOfferUrlSourceType.LD]: 'LD',
};

export interface IReferralProgramModel {
  referralPayoutModel: string;
  payoutModelValue: string;
  conditions: IValue[];
}

export interface IReferralProgramSettings {
  models: IReferralProgramModel[];
  program_type: EReferralProgramType;
}

export interface ICurrencyIntegration {
  name: string;
  type: string;
  id: number;
}

export type CRMType = 'affiliate' | 'advertiser';

export enum EConversionProfileFormats {
  MONEY = 'money',
  NUMBER = 'number',
  TEXT = 'text',
  DATE = 'date',
  EMAIL = 'email',
  BOOLEAN = 'boolean',
  MONEY_WITH_CURRENCY = 'money_with_currency',
}

export enum LANGUAGE_CONSTANT {
  EN = 'en',
  PT = 'pt',
}
export enum INVOICE_STATUSES {
  WAITING = 'waiting',
  PENDING = 'pending',
  PAID = 'paid',
  DECLINED = 'declined',
  DRAFT = 'draft',
  APPROVED = 'approved',
  ARRIVED = 'arrived',
  SENT = 'sent',
}

export enum EARNING_STATUSES {
  APPLIED = 'applied',
  PAID = 'paid',
  DRAFT = 'draft',
  ARCHIVED = 'archived',
}

export enum AFFILIATE_REQUEST_STATUSES {
  REQUESTED = 'requested',
  APPROVED = 'approved',
  DECLINED = 'declined',
}

export enum STICKY_BUTTON_TYPE {
  AUTH = 'auth',
  EDIT_ROW_UNSECURE = 'edit-row-unsecure',
  EDIT_ROW_PROTECTED_FIELD = 'edit-row-protected-field',
  DROPDOWN_BUTTON = 'dropdown-button',
  EDIT_ROW = 'edit-row',
  CALLBACK_BUTTON = 'callback-button',
}

export enum CRM_TYPE {
  AFFILIATE = 'affiliate',
  ADVERTISER = 'advertiser',
}

export enum LOG_METHODS {
  POST = 'POST',
  GET = 'GET',
  PUT = 'PUT',
  DELETE = 'DELETE',
}
